import { lazy, StrictMode, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { App } from "./app/App";
import { ErrorBoundary } from "./app/ErrorBoundary";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 30_000
    }
  }
});

/**
 * CC-1d — admin audit console route. The app is a single-page editor with
 * no client router; the lone non-editor surface is resolved by a pathname
 * gate here (the documented UI_ARCHITECTURE.md route map remains
 * aspirational otherwise). Lazy so the admin bundle never loads for the
 * editor path — and vice versa.
 */
const AdminAuditConsole = lazy(() =>
  import("./app/admin/AdminAuditConsole").then((module) => ({
    default: module.AdminAuditConsole
  }))
);

function resolveRootView(): "admin-audit" | "editor" {
  const pathname = globalThis.location?.pathname ?? "/";
  return pathname.replace(/\/+$/u, "") === "/admin/audit"
    ? "admin-audit"
    : "editor";
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {resolveRootView() === "admin-audit" ? (
          <Suspense fallback={null}>
            <AdminAuditConsole />
          </Suspense>
        ) : (
          <App />
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
